import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Plus, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Status dropdown with "＋ Add new status..." option.
 * Props:
 *   value        - current status value
 *   onValueChange - called with new status value
 *   allStatuses  - array of { value, label, color } from useLeadStatuses()
 *   addStatus    - async fn(label) => value, from useLeadStatuses()
 */
export default function StatusSelect({ value, onValueChange, allStatuses, addStatus }) {
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    setSaving(true);
    try {
      const saved = await addStatus(newLabel);
      if (saved) {
        onValueChange(saved);
        toast.success(`Status "${saved}" added`);
      }
      setNewLabel('');
      setAdding(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <Select
        value={value}
        onValueChange={(v) => {
          if (v === '__add_new__') {
            setAdding(true);
          } else {
            setAdding(false);
            onValueChange(v);
          }
        }}
      >
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          {allStatuses.map(s => (
            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
          ))}
          <SelectItem value="__add_new__" className="text-indigo-600 font-medium border-t mt-1 pt-1">
            <span className="flex items-center gap-1"><Plus className="h-3 w-3" /> Add new status...</span>
          </SelectItem>
        </SelectContent>
      </Select>

      {adding && (
        <div className="flex items-center gap-2">
          <Input
            autoFocus
            placeholder="e.g. Negotiating, Follow Up..."
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') { setAdding(false); setNewLabel(''); } }}
            className="h-8 text-sm"
          />
          <Button size="sm" className="h-8 px-2" onClick={handleAdd} disabled={saving || !newLabel.trim()}>
            <Check className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
}
